import { format, parseISO, getDay } from 'date-fns';
import { DownloadIcon } from 'lucide-react';
import type React from 'react';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { customerReportService } from '../services/customerReportService';
import { colors } from '../theme/colors';
import { useAuth } from '../contexts/AuthContext';
import { exportToPDF, type EmployeeData } from '../utils/leistungsnachweisExports';

interface IsolatedReportPreviewProps {
  projectNumber: string;
  projectName: string;
  month: string;
  employeeId: string;
  employeeName?: string;
  selectedOrder?: string;
  customOrderNumber?: string;
  onBack: () => void;
  showBackButton?: boolean;
}

const GERMAN_DAY_NAMES = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

const GERMAN_MONTH_NAMES = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

const getDayName = (date: Date): string => GERMAN_DAY_NAMES[getDay(date)];

const formatDate = (isoDate: string): string => format(parseISO(isoDate), 'dd.MM.yyyy');

const formatMonthYear = (monthYear: string): string => {
  const [year, month] = monthYear.split('-');
  const monthIndex = parseInt(month, 10) - 1;
  return `${GERMAN_MONTH_NAMES[monthIndex]} ${year}`;
};

const getTaskCode = (task: string): string => {
  const trimmed = (task ?? '').trim();
  const firstSpace = trimmed.indexOf(' ');
  return firstSpace === -1 ? trimmed : trimmed.substring(0, firstSpace);
};

const isTravelTimeTask = (taskCode: string): boolean => taskCode === '001';

const MM_TO_PX = 3.779527559;

const ISOLATED_REPORT_HOURS_STD_COL_PX = 50;
const ISOLATED_REPORT_REISEZEIT_COL_PX = 65;

function countDescriptionWrappedLines(description: string, charsPerLine: number): number {
  const words = description.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 1;

  let lines = 0;
  let lineLen = 0;

  for (const word of words) {
    if (word.length > charsPerLine) {
      if (lineLen > 0) {
        lines++;
        lineLen = 0;
      }
      lines += Math.max(1, Math.ceil(word.length / charsPerLine));
      const rem = word.length % charsPerLine;
      lineLen = rem === 0 ? 0 : rem;
      continue;
    }
    const need = lineLen === 0 ? word.length : lineLen + 1 + word.length;
    if (need > charsPerLine) {
      lines++;
      lineLen = word.length;
    } else {
      lineLen = need;
    }
  }
  if (lineLen > 0) lines++;
  return Math.max(1, lines);
}

function estimateIsolatedRowHeight(
  entry: { description: string },
  tableWidthPx: number,
  includeTravelTime: boolean
): number {
  const baseRowHeight = 32;
  const cellPaddingVertical = 8;
  const fontSize = 12;
  const lineHeight = fontSize * 1.2;

  if (!entry.description || entry.description.trim() === '') {
    return baseRowHeight;
  }

  const dateColWidth = 90;
  const dayColWidth = 90;
  const taskColWidth = 180;
  const hoursColWidth = ISOLATED_REPORT_HOURS_STD_COL_PX;
  const travelTimeColWidth = includeTravelTime ? ISOLATED_REPORT_REISEZEIT_COL_PX : 0;
  const cellPaddingHorizontal = 8;
  const totalFixedColsWidth = dateColWidth + dayColWidth + taskColWidth + hoursColWidth + travelTimeColWidth;
  const totalCellPadding = cellPaddingHorizontal * (includeTravelTime ? 12 : 10);

  const descriptionColWidth = Math.max(100, tableWidthPx - totalFixedColsWidth - totalCellPadding);

  const avgCharWidth = fontSize * 0.55;
  const charsPerLine = Math.max(8, Math.floor((descriptionColWidth / avgCharWidth) * 0.92));

  const actualLines = countDescriptionWrappedLines(entry.description, charsPerLine);

  const contentHeight = actualLines * lineHeight;
  const totalHeight = Math.max(baseRowHeight, contentHeight + cellPaddingVertical * 2);

  return Math.ceil(totalHeight * 1.15);
}

function stuffPageSlack<T extends { date: string }>(
  pages: T[][],
  visibleEntries: T[],
  pageBodyBudgetPx: number,
  getRowHeight: (entry: T, globalIndex: number) => number
): T[][] {
  if (pages.length < 2) {
    return pages;
  }

  const indexByEntry = new Map<T, number>();
  visibleEntries.forEach((e, idx) => {
    indexByEntry.set(e, idx);
  });

  const result = pages.map((p) => [...p]);

  const rowHeight = (entry: T): number => {
    const gi = indexByEntry.get(entry);
    if (gi === undefined) return 0;
    return getRowHeight(entry, gi);
  };

  for (let i = 0; i <= result.length - 2; i++) {
    let slack = pageBodyBudgetPx;
    for (let r = 0; r < result[i].length; r++) {
      slack -= rowHeight(result[i][r]);
    }

    const pageI = result[i];
    const pageIp1 = result[i + 1];

    while (slack > 0 && pageIp1.length > 0) {
      const lastDate = pageI[pageI.length - 1].date;
      const candidate = pageIp1[0];
      if (candidate.date !== lastDate) {
        break;
      }
      const h = rowHeight(candidate);
      if (h <= 0 || h > slack) {
        break;
      }
      pageI.push(pageIp1.shift()!);
      slack -= h;
    }

    while (slack > 0 && pageIp1.length > 0) {
      const candidate = pageIp1[0];
      const h = rowHeight(candidate);
      if (h <= 0 || h > slack) {
        break;
      }
      pageI.push(pageIp1.shift()!);
      slack -= h;
    }
  }

  return result.filter((p) => p.length > 0);
}

function fixLastPageBodyOverflow<T>(
  pages: T[][],
  bodyBudgetPx: number,
  getRowHeight: (entry: T, globalIndex: number) => number,
  lastPageReservePx: number
): T[][] {
  if (pages.length === 0) return pages;
  const slackPx = 12;

  const globalIndexFor = (pgs: T[][], pageIdx: number, rowIdx: number): number => {
    let s = 0;
    for (let i = 0; i < pageIdx; i++) s += pgs[i].length;
    return s + rowIdx;
  };

  const sumHeightsOnPage = (pgs: T[][], pageIdx: number): number => {
    const start = globalIndexFor(pgs, pageIdx, 0);
    let sum = 0;
    pgs[pageIdx].forEach((e, j) => {
      sum += getRowHeight(e, start + j);
    });
    return sum;
  };

  let result = pages.map((p) => [...p]);

  for (let iter = 0; iter < 1000; iter++) {
    const li = result.length - 1;
    const sum = sumHeightsOnPage(result, li);
    if (sum + lastPageReservePx <= bodyBudgetPx + slackPx) {
      return result.filter((p) => p.length > 0);
    }
    if (result[li].length <= 1) {
      return result.filter((p) => p.length > 0);
    }
    const moved = result[li].pop()!;
    if (!result[li + 1]) {
      result.push([]);
    }
    result[li + 1].unshift(moved);
  }
  return result.filter((p) => p.length > 0);
}

export const IsolatedReportPreview: React.FC<IsolatedReportPreviewProps> = ({
  projectNumber,
  projectName,
  month,
  employeeId,
  employeeName: propEmployeeName,
  selectedOrder = '',
  customOrderNumber = '',
  onBack,
  showBackButton = true,
}) => {
  const { user } = useAuth();
  const userRole = user?.role?.toLowerCase().trim() || '';
  const isAdmin = userRole === 'admin';
  const canViewSalesRate = isAdmin || ['project manager', 'project_manager', 'assistant', 'deputy', 'finance'].includes(userRole);

  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isExportingExcel, setIsExportingExcel] = useState<boolean>(false);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const measureBodyRef = useRef<HTMLTableSectionElement>(null);
  const firstPageTableWrapRef = useRef<HTMLDivElement>(null);
  const [rowHeights, setRowHeights] = useState<number[] | null>(null);
  const [measuredTableBodyAvailablePx, setMeasuredTableBodyAvailablePx] = useState<number | null>(null);

  const orderDisplay = useMemo(() => {
    if (customOrderNumber && customOrderNumber.trim() !== '') {
      return customOrderNumber;
    }
    if (!selectedOrder || selectedOrder === '') {
      return '-';
    }
    if (selectedOrder === '__NOT_APPLICABLE__') {
      return 'Not Applicable';
    }
    return selectedOrder;
  }, [selectedOrder, customOrderNumber]);

  const {
    data: reportData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['customer-report-isolated', projectNumber, month, employeeId, selectedOrder],
    queryFn: () => customerReportService.getIsolatedReportByEmployee(projectNumber, month, employeeId, selectedOrder || ''),
    enabled: !!projectNumber && !!month && !!employeeId,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  const visibleEntries = useMemo(() => {
    if (!reportData?.entries) return [];
    if (!reportData.include_travel_time) {
      return reportData.entries
        .filter((e) => !isTravelTimeTask(getTaskCode(e.task)))
        .map((e) => {
          let day = '';
          try {
            day = getDayName(parseISO(e.date));
          } catch {
            day = e.day ?? '';
          }
          return { ...e, day };
        });
    }
    const travelTimeByDate = new Map<string, number>();
    reportData.entries.forEach((entry) => {
      if (isTravelTimeTask(getTaskCode(entry.task))) {
        const date = entry.date;
        const current = travelTimeByDate.get(date) ?? 0;
        travelTimeByDate.set(date, current + entry.travel_time);
      }
    });
    type Entry = (typeof reportData.entries)[0];
    const grouped: Entry[] = [];
    const datesWithNonTravel = new Set<string>();
    reportData.entries.forEach((entry) => {
      if (isTravelTimeTask(getTaskCode(entry.task))) return;
      datesWithNonTravel.add(entry.date);
      grouped.push({ ...entry, travel_time: 0 });
    });
    travelTimeByDate.forEach((travelTime, date) => {
      if (!datesWithNonTravel.has(date) && travelTime > 0) {
        let day = '';
        try {
          day = getDayName(parseISO(date));
        } catch {
          day = '';
        }
        grouped.push({
          date,
          day,
          task: '',
          description: '',
          hours: 0,
          travel_time: travelTime,
          sales_rate: 0,
        });
      }
    });
    grouped.sort((a, b) => {
      const d = a.date.localeCompare(b.date);
      if (d !== 0) return d;
      return (a.task ?? '').localeCompare(b.task ?? '');
    });
    let lastDate = '';
    return grouped.map((entry) => {
      const isFirstForDate = entry.date !== lastDate;
      if (isFirstForDate) lastDate = entry.date;
      const travelTime = isFirstForDate ? (travelTimeByDate.get(entry.date) ?? 0) : 0;
      let day = '';
      try {
        day = getDayName(parseISO(entry.date));
      } catch {
        day = entry.day ?? '';
      }
      return { ...entry, travel_time: travelTime, day };
    });
  }, [reportData?.entries, reportData?.include_travel_time]);

  const descriptionColumnWidth = useMemo(() => {
    const pageWidthPx = 210 * MM_TO_PX;
    const contentPadding = 24;
    const tableWidthPx = pageWidthPx - contentPadding * 2;

    const dateColWidth = 90;
    const dayColWidth = 90;
    const taskColWidth = 180;
    const hoursColWidth = ISOLATED_REPORT_HOURS_STD_COL_PX;
    const travelTimeColWidth = reportData?.include_travel_time ? ISOLATED_REPORT_REISEZEIT_COL_PX : 0;

    const fixedColumnsWidth = dateColWidth + dayColWidth + taskColWidth + hoursColWidth + travelTimeColWidth;
    const descriptionWidth = Math.max(100, tableWidthPx - fixedColumnsWidth);

    return `${descriptionWidth}px`;
  }, [reportData?.include_travel_time]);

  const calculatePages = useMemo(() => {
    if (!reportData || visibleEntries.length === 0) {
      return [];
    }

    const pageHeight = 297;
    const pageHeightPx = pageHeight * MM_TO_PX;
    const pageWidth = 210;
    const pageWidthPx = pageWidth * MM_TO_PX;

    const headerHeightPx = 165;
    const tableHeaderHeightPx = 28;
    const tableTfootHeightPx = 45;
    const signaturesHeightPx = 60;
    const footerHeightPx = 20;
    const contentPadding = 24;
    const contentPaddingPx = contentPadding;

    const tableWidthPx = pageWidthPx - (contentPaddingPx * 2);

    const summaryHeightPx = reportData.show_isolated_summary && reportData.summary && reportData.summary.length > 0 ? 200 : 0;

    const lastPageBodyReservePx =
      signaturesHeightPx + footerHeightPx + summaryHeightPx + tableTfootHeightPx;

    const contentVerticalPaddingPx = contentPadding * 2;
    const heightSafetyMarginPx = 20;
    const pageIndexRowPx = 30;
    const formulaTableBodyBudgetPx =
      pageHeightPx -
      headerHeightPx -
      tableHeaderHeightPx -
      contentVerticalPaddingPx -
      heightSafetyMarginPx -
      pageIndexRowPx;

    type EntryType = typeof visibleEntries[0];

    const useMeasuredHeights =
      rowHeights !== null && rowHeights.length === visibleEntries.length;

    const packRows = (pageTableAvailableHeight: number): EntryType[][] => {
      const pagesInner: EntryType[][] = [];
      let currentPageRowsInner: EntryType[] = [];
      let currentPageHeightInner = pageTableAvailableHeight;

      visibleEntries.forEach((entry, index) => {
        const measured = useMeasuredHeights ? rowHeights[index] : undefined;
        const entryHeight =
          measured !== undefined && measured > 0
            ? measured
            : estimateIsolatedRowHeight(entry, tableWidthPx, reportData.include_travel_time);

        const remainingEntries = visibleEntries.length - index - 1;
        const willBeLastPage = remainingEntries === 0;

        const neededReserved = willBeLastPage ? lastPageBodyReservePx : 0;

        const spaceNeeded = entryHeight + neededReserved;
        const hasEnoughSpace = currentPageHeightInner >= spaceNeeded;

        if (!hasEnoughSpace && currentPageRowsInner.length > 0) {
          pagesInner.push([...currentPageRowsInner]);
          currentPageRowsInner = [];
          currentPageHeightInner = pageTableAvailableHeight;

          const newNeededReserved = willBeLastPage ? lastPageBodyReservePx : 0;
          const newSpaceNeeded = entryHeight + newNeededReserved;

          if (currentPageHeightInner >= newSpaceNeeded) {
            currentPageRowsInner.push(entry);
            currentPageHeightInner -= entryHeight;
            if (willBeLastPage) {
              currentPageHeightInner -= newNeededReserved;
            }
          } else {
            currentPageRowsInner.push(entry);
            currentPageHeightInner -= entryHeight;
          }
        } else {
          currentPageRowsInner.push(entry);
          currentPageHeightInner -= entryHeight;
          if (willBeLastPage) {
            currentPageHeightInner -= neededReserved;
          }
        }
      });

      if (currentPageRowsInner.length > 0) {
        pagesInner.push(currentPageRowsInner);
      }

      return pagesInner;
    };

    const getRowHeight = (entry: EntryType, globalIndex: number): number => {
      const measured = useMeasuredHeights ? rowHeights[globalIndex] : undefined;
      return measured !== undefined && measured > 0
        ? measured
        : estimateIsolatedRowHeight(entry, tableWidthPx, reportData.include_travel_time);
    };

    let packedPages = packRows(formulaTableBodyBudgetPx);
    let packBudgetUsed = formulaTableBodyBudgetPx;

    if (
      measuredTableBodyAvailablePx != null &&
      measuredTableBodyAvailablePx > formulaTableBodyBudgetPx + 0.5 &&
      packedPages.length > 1
    ) {
      const pagesFromDom = packRows(measuredTableBodyAvailablePx);
      if (pagesFromDom.length < packedPages.length && pagesFromDom.length > 1) {
        packedPages = pagesFromDom;
        packBudgetUsed = measuredTableBodyAvailablePx;
      }
    }

    if (packedPages.length >= 2) {
      packedPages = stuffPageSlack(packedPages, visibleEntries, packBudgetUsed, getRowHeight);
    }

    packedPages = fixLastPageBodyOverflow(packedPages, packBudgetUsed, getRowHeight, lastPageBodyReservePx);

    return packedPages;
  }, [reportData, visibleEntries, rowHeights, measuredTableBodyAvailablePx]);

  useLayoutEffect(() => {
    if (!reportData || visibleEntries.length === 0) {
      setRowHeights([]);
      return;
    }

    let cancelled = false;

    const readHeights = () => {
      const tbody = measureBodyRef.current;
      if (!tbody || cancelled) return;
      const trs = tbody.querySelectorAll(':scope > tr');
      if (trs.length !== visibleEntries.length) return;
      const next: number[] = [];
      trs.forEach((tr) => {
        next.push(tr.getBoundingClientRect().height);
      });
      if (!cancelled) {
        setRowHeights(next);
      }
    };

    const run = async () => {
      if (typeof document !== 'undefined' && 'fonts' in document && document.fonts?.ready) {
        try {
          await document.fonts.ready;
        } catch {
          /* ignore */
        }
      }
      if (cancelled) return;
      requestAnimationFrame(() => {
        if (!cancelled) readHeights();
      });
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [reportData, visibleEntries, descriptionColumnWidth, reportData?.include_travel_time]);

  useLayoutEffect(() => {
    if (!reportData || visibleEntries.length === 0) {
      setMeasuredTableBodyAvailablePx(null);
      return;
    }

    let cancelled = false;
    requestAnimationFrame(() => {
      if (cancelled) return;
      if (calculatePages.length <= 1) {
        setMeasuredTableBodyAvailablePx(null);
        return;
      }
      const wrap = firstPageTableWrapRef.current;
      if (!wrap) return;
      const thead = wrap.querySelector('thead');
      const theadH = thead?.getBoundingClientRect().height ?? 0;
      const tbodyBudgetPx = Math.max(0, wrap.clientHeight - theadH - 1);
      setMeasuredTableBodyAvailablePx(tbodyBudgetPx);
    });

    return () => {
      cancelled = true;
    };
  }, [reportData, visibleEntries.length, calculatePages, rowHeights]);

  const selectedEmployeeData = useMemo<EmployeeData | null>(() => {
    if (!reportData) {
      return null;
    }
    return {
      employeeId,
      employeeName: propEmployeeName || reportData.employee_name,
      projectName,
      orderNumber: orderDisplay,
      month: formatMonthYear(month),
    };
  }, [employeeId, propEmployeeName, reportData, projectName, orderDisplay, month]);

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem',
            color: colors.neutral.grey[600],
          }}
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              border: `4px solid ${colors.neutral.grey[200]}`,
              borderTop: `4px solid ${colors.accent.blue}`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <p>Loading report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: '1rem',
          backgroundColor: colors.accent.red + '10',
          color: colors.accent.red,
          borderRadius: '6px',
          marginBottom: '1rem',
        }}
      >
        Error loading report: {error instanceof Error ? error.message : 'Unknown error'}
        {showBackButton && (
          <button
            onClick={onBack}
            style={{
              marginLeft: '1rem',
              padding: '0.5rem 1rem',
              backgroundColor: colors.accent.blue,
              color: colors.neutral.white,
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            Back
          </button>
        )}
      </div>
    );
  }

  if (!reportData) {
    return null;
  }

  const totals = visibleEntries.reduce(
    (acc, entry) => {
      acc.hours += entry.hours;
      acc.travelTime += entry.travel_time;
      return acc;
    },
    { hours: 0, travelTime: 0 }
  );

  const handleExportPDF = async () => {
    if (!pdfContainerRef.current || !selectedEmployeeData || calculatePages.length === 0) {
      return;
    }
    await exportToPDF(pdfContainerRef, selectedEmployeeData, setIsExporting);
  };

  const handleExportExcel = async () => {
    setIsExportingExcel(true);
    try {
      await customerReportService.exportIsolatedReportExcel(
        projectNumber,
        month,
        employeeId,
        selectedOrder || customOrderNumber || undefined
      );
    } finally {
      setIsExportingExcel(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        padding: '2rem 0',
        gap: '2rem',
      }}
    >
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '0.75rem',
          marginBottom: '-1rem',
        }}
      >
        {calculatePages.length > 0 && (
          <button
            onClick={handleExportExcel}
            disabled={isExportingExcel}
            style={{
              padding: '0.625rem 1.25rem',
              height: '2.5rem',
              backgroundColor: isExportingExcel ? colors.neutral.grey[400] : colors.accent.blue,
              color: colors.neutral.white,
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: isExportingExcel ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
            }}
          >
            {isExportingExcel ? (
              <>
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    border: `2px solid ${colors.neutral.white}`,
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
                Excel wird erstellt...
              </>
            ) : (
              <>Als Excel exportieren</>
            )}
          </button>
        )}
        {calculatePages.length > 0 && (
          <button
            onClick={handleExportPDF}
            disabled={isExporting}
            style={{
              padding: '0.625rem 1.25rem',
              height: '2.5rem',
              backgroundColor: isExporting ? colors.neutral.grey[400] : colors.accent.blue,
              color: colors.neutral.white,
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: isExporting ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s, box-shadow 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
            }}
            onMouseEnter={(e) => {
              if (!isExporting) {
                e.currentTarget.style.backgroundColor = colors.accent.blueHover;
                e.currentTarget.style.boxShadow = '0 6px 8px rgba(0, 0, 0, 0.15), 0 4px 6px rgba(0, 0, 0, 0.1)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isExporting) {
                e.currentTarget.style.backgroundColor = colors.accent.blue;
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)';
              }
            }}
          >
            {isExporting ? (
              <>
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    border: `2px solid ${colors.neutral.white}`,
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
                PDF wird erstellt...
              </>
            ) : (
              <>
                <DownloadIcon size={18} />
                Als PDF exportieren
              </>
            )}
          </button>
        )}
        {showBackButton && (
          <button
            onClick={onBack}
            style={{
              backgroundColor: colors.neutral.grey[400],
              color: colors.neutral.white,
              border: 'none',
              borderRadius: '6px',
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.neutral.grey[500];
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.neutral.grey[400];
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Exit Preview
          </button>
        )}
      </div>

      {visibleEntries.length > 0 && (
        <div
          aria-hidden
          style={{
            position: 'fixed',
            left: -10000,
            top: 0,
            visibility: 'hidden',
            pointerEvents: 'none',
            zIndex: -1,
            width: '210mm',
            boxSizing: 'border-box',
          }}
        >
          <div style={{ padding: '1.5rem', boxSizing: 'border-box' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                tableLayout: 'fixed',
              }}
            >
              <thead>
                <tr style={{ borderBottom: `2px solid ${colors.neutral.grey[300]}` }}>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '0.5rem',
                      color: colors.secondary.text,
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      width: '90px',
                      verticalAlign: 'top',
                    }}
                  >
                    Datum
                  </th>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '0.5rem',
                      color: colors.secondary.text,
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      width: '90px',
                      verticalAlign: 'top',
                    }}
                  >
                    Tag
                  </th>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '0.5rem',
                      color: colors.secondary.text,
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      width: '180px',
                      verticalAlign: 'top',
                    }}
                  >
                    Task
                  </th>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '0.5rem',
                      color: colors.secondary.text,
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      verticalAlign: 'top',
                      width: descriptionColumnWidth,
                    }}
                  >
                    Beschreibung
                  </th>
                  <th
                    style={{
                      textAlign: 'right',
                      padding: '0.5rem',
                      color: colors.secondary.text,
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      width: `${ISOLATED_REPORT_HOURS_STD_COL_PX}px`,
                      verticalAlign: 'top',
                    }}
                  >
                    Stunden (Std.)
                  </th>
                  {reportData.include_travel_time && (
                    <th
                      style={{
                        textAlign: 'right',
                        padding: '0.5rem',
                        color: colors.secondary.text,
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        width: `${ISOLATED_REPORT_REISEZEIT_COL_PX}px`,
                        verticalAlign: 'top',
                      }}
                    >
                      Reisezeit (Std.)
                    </th>
                  )}
                </tr>
              </thead>
              <tbody ref={measureBodyRef}>
                {visibleEntries.map((entry, globalIndex) => {
                  const showDate =
                    globalIndex === 0 ||
                    visibleEntries[globalIndex - 1].date !== entry.date;
                  return (
                    <tr
                      key={`measure-${entry.date}-${globalIndex}`}
                      style={{
                        borderBottom: `1px solid ${colors.neutral.grey[200]}`,
                      }}
                    >
                      <td style={{ padding: '0.5rem', fontSize: '0.75rem', width: '90px', verticalAlign: 'top' }}>
                        {showDate ? formatDate(entry.date) : ''}
                      </td>
                      <td style={{ padding: '0.5rem', fontSize: '0.75rem', width: '90px', verticalAlign: 'top' }}>
                        {showDate ? entry.day : ''}
                      </td>
                      <td style={{ padding: '0.5rem', fontSize: '0.75rem', width: '180px', verticalAlign: 'top' }}>
                        {entry.task !== '001 - Traveltime' && entry.task}
                      </td>
                      <td
                        style={{
                          padding: '0.5rem',
                          fontSize: '0.75rem',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal',
                          verticalAlign: 'top',
                          width: descriptionColumnWidth,
                        }}
                      >
                        {entry.task !== '001 - Traveltime' && entry.description}
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem', width: `${ISOLATED_REPORT_HOURS_STD_COL_PX}px`, verticalAlign: 'top' }}>
                        {entry.hours.toFixed(2)}
                      </td>
                      {reportData.include_travel_time && (
                        <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem', width: `${ISOLATED_REPORT_REISEZEIT_COL_PX}px`, verticalAlign: 'top' }}>
                          {showDate ? entry.travel_time.toFixed(2) : ''}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {calculatePages.map((pageRows, pageIndex) => {
        const isLastPage = pageIndex === calculatePages.length - 1;

        return (
          <div
            key={pageIndex}
            ref={pageIndex === 0 ? pdfContainerRef : null}
            data-pdf-export="true"
            style={{
              width: '210mm',
              height: '297mm',
              backgroundColor: colors.neutral.white,
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              padding: '0',
              margin: '0',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                backgroundColor: '#9DB4C0',
                padding: '2rem',
                borderRadius: '0',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <img
                    src="/assets/logo/PPE-intelligence_Logo_rgb_Black.svg"
                    alt="PPF Intelligence Logo"
                    style={{ height: '40px', marginBottom: '0.5rem' }}
                  />
                  <h1 style={{ color: 'white', margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
                    Leistungsnachweis
                  </h1>
                </div>
                <div style={{ color: 'white', textAlign: 'right', fontSize: '0.875rem' }}>
                  <div style={{ marginBottom: '0.375rem' }}>
                    <span style={{ opacity: 0.8 }}>Name:</span> <strong>{propEmployeeName || reportData.employee_name}</strong>
                  </div>
                  <div style={{ marginBottom: '0.375rem' }}>
                    <span style={{ opacity: 0.8 }}>Projektname:</span> <strong>{projectName}</strong>
                  </div>
                  <div>
                    <span style={{ opacity: 0.8 }}>Bestellnummer:</span> <strong>{orderDisplay}</strong>
                  </div>
                  <div>
                    <span style={{ opacity: 0.8 }}>Monat:</span> <strong>{formatMonthYear(month)}</strong>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ padding: '1.5rem', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
              <div
                ref={pageIndex === 0 ? firstPageTableWrapRef : undefined}
                style={{ overflow: 'visible', flex: '0 1 auto', minHeight: 0 }}
              >
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    tableLayout: 'fixed',
                  }}
                >
                  <thead
                    style={{
                      display: 'table-header-group',
                      position: 'relative',
                    }}
                  >
                    <tr style={{ borderBottom: `2px solid ${colors.neutral.grey[300]}` }}>
                      <th style={{ textAlign: 'left', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600', width: '90px', verticalAlign: 'top' }}>
                        Datum
                      </th>
                      <th style={{ textAlign: 'left', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600', width: '90px', verticalAlign: 'top' }}>
                        Tag
                      </th>
                      <th style={{ textAlign: 'left', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600', width: '180px', verticalAlign: 'top' }}>
                        Task
                      </th>
                      <th style={{ textAlign: 'left', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600', verticalAlign: 'top', width: descriptionColumnWidth }}>
                        Beschreibung
                      </th>
                      <th style={{ textAlign: 'right', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600', width: `${ISOLATED_REPORT_HOURS_STD_COL_PX}px`, verticalAlign: 'top' }}>
                        Stunden (Std.)
                      </th>
                      {reportData.include_travel_time && (
                        <th style={{ textAlign: 'right', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600', width: `${ISOLATED_REPORT_REISEZEIT_COL_PX}px`, verticalAlign: 'top' }}>
                          Reisezeit (Std.)
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((entry, rowIndex) => {
                      const globalIndex = calculatePages.slice(0, pageIndex).reduce((sum, p) => sum + p.length, 0) + rowIndex;
                      const showDate =
                        globalIndex === 0 || (globalIndex > 0 && visibleEntries[globalIndex - 1].date !== entry.date);
                      
                      return (
                        <tr
                          key={`${entry.date}-${globalIndex}`}
                          style={{
                            borderBottom: `1px solid ${colors.neutral.grey[200]}`,
                          }}
                        >
                          <td style={{ padding: '0.5rem', fontSize: '0.75rem', width: '90px', verticalAlign: 'top' }}>{showDate ? formatDate(entry.date) : ''}</td>
                          <td style={{ padding: '0.5rem', fontSize: '0.75rem', width: '90px', verticalAlign: 'top' }}>{showDate ? entry.day : ''}</td>
                          <td style={{ padding: '0.5rem', fontSize: '0.75rem', width: '180px', verticalAlign: 'top' }}>{entry.task !== '001 - Traveltime' && entry.task}</td>
                          <td style={{ 
                            padding: '0.5rem', 
                            fontSize: '0.75rem',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            whiteSpace: 'normal',
                            verticalAlign: 'top',
                            width: descriptionColumnWidth
                          }}>{entry.task !== '001 - Traveltime' && entry.description}</td>
                          <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem', width: `${ISOLATED_REPORT_HOURS_STD_COL_PX}px`, verticalAlign: 'top' }}>
                            {entry.hours.toFixed(2)}
                          </td>
                          {reportData.include_travel_time && (
                            <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem', width: `${ISOLATED_REPORT_REISEZEIT_COL_PX}px`, verticalAlign: 'top' }}>
                              {showDate ? entry.travel_time.toFixed(2) : ''}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                  {isLastPage && (
                    <tfoot>
                      <tr style={{ borderTop: `2px solid ${colors.neutral.grey[300]}` }}>
                        <td colSpan={4} style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.8rem', verticalAlign: 'top' }}>
                          <strong>Total</strong>
                        </td>
                        <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.8rem', width: `${ISOLATED_REPORT_HOURS_STD_COL_PX}px`, verticalAlign: 'top' }}>
                          <strong>{totals.hours.toFixed(2)}</strong>
                        </td>
                        {reportData.include_travel_time && (
                          <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.8rem', width: `${ISOLATED_REPORT_REISEZEIT_COL_PX}px`, verticalAlign: 'top' }}>
                            <strong>{totals.travelTime.toFixed(2)}</strong>
                          </td>
                        )}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              {isLastPage && reportData.show_isolated_summary && reportData.summary && reportData.summary.length > 0 && (
                <div
                  style={{
                    marginTop: '1rem',
                    marginBottom: '2rem',
                    borderTop: `2px solid ${colors.neutral.grey[200]}`,
                  }}
                >
                  <p style={{ marginBottom: '0.8rem', color: colors.neutral.grey[700], fontSize: '0.875rem' }}>
                    Zusammenfassung
                  </p>
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                    }}
                  >
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${colors.neutral.grey[300]}` }}>
                        <th style={{ textAlign: 'left', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600' }}>
                          Mitarbeiter Name
                        </th>
                        <th style={{ textAlign: 'right', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600' }}>
                          Billable h
                        </th>
                        {reportData.include_travel_time && (
                          <th style={{ textAlign: 'right', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600' }}>
                            Travel Time Billable h
                          </th>
                        )}
                        <th style={{ textAlign: 'center', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600' }}>
                          Worktype
                        </th>
                        <th style={{ textAlign: 'right', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600' }}>
                          Verkaufssatz (EUR)
                        </th>
                        <th style={{ textAlign: 'right', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600' }}>
                          Umsatz {reportData.include_travel_time ? '(inklusive Travel h)' : ''} (EUR)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.summary.map((summaryItem, index) => (
                        <tr key={index} style={{ borderBottom: `1px solid ${colors.neutral.grey[100]}` }}>
                          <td style={{ padding: '0.5rem', fontSize: '0.75rem' }}>{index === 0 ? (propEmployeeName || reportData.employee_name) : ''}</td>
                          <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem' }}>
                            {summaryItem.billable_hours.toFixed(2)}
                          </td>
                          {reportData.include_travel_time && (
                            <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem' }}>
                              {summaryItem.travel_time_hours.toFixed(2)}
                            </td>
                          )}
                          <td style={{ padding: '0.5rem', textAlign: 'center', fontSize: '0.75rem' }}>
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '9999px',
                                fontSize: '0.7rem',
                                backgroundColor: summaryItem.worktype === 'office' ? colors.accent.blue + '20' : colors.accent.green + '20',
                                color: summaryItem.worktype === 'office' ? colors.accent.blue : colors.accent.green,
                              }}
                            >
                              {summaryItem.worktype.toUpperCase() || 'N/A'}
                            </span>
                          </td>
                          <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem' }}>
                            {canViewSalesRate ? `€${summaryItem.sales_rate.toFixed(2)}` : 'N/A'}
                          </td>
                          <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: '600' }}>
                            €{summaryItem.turnover.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: `2px solid ${colors.neutral.grey[300]}` }}>
                        <td colSpan={reportData.include_travel_time ? 5 : 4} style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.8rem' }}>
                          <strong>Total</strong>
                        </td>
                        <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.8rem', fontWeight: '600' }}>
                          €{reportData.summary.reduce((acc, s) => acc + s.turnover, 0).toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {isLastPage && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '1.5rem',
                    marginTop: 'auto',
                    paddingTop: '5rem',
                    marginBottom: '1rem',
                    flexShrink: 0,
                  }}
                >
                  <div>
                    <div
                      style={{
                        borderBottom: `1px solid ${colors.neutral.grey[400]}`,
                        paddingBottom: '0.5rem',
                        marginBottom: '0.5rem',
                        height: '32px',
                      }}
                    />
                    <div style={{ fontSize: '0.7rem', color: colors.secondary.text }}>Unterschrift Projektleiter</div>
                  </div>
                  <div>
                    <div
                      style={{
                        borderBottom: `1px solid ${colors.neutral.grey[400]}`,
                        paddingBottom: '0.5rem',
                        marginBottom: '0.5rem',
                        height: '32px',
                      }}
                    />
                    <div style={{ fontSize: '0.7rem', color: colors.secondary.text }}>Unterschrift Kunde</div>
                  </div>
                </div>
              )}

              <div
                style={{
                  marginTop: isLastPage ? '0.1rem' : 'auto',
                  paddingTop: '0.3rem',
                  borderTop: `1px solid ${colors.neutral.grey[200]}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '0.5rem',
                  color: colors.neutral.grey[500],
                  flexShrink: 0,
                }}
              >
                <span>Seite {pageIndex + 1}/{calculatePages.length}</span>
                <span>Erstellt am: {format(new Date(), 'dd.MM.yyyy HH:mm')} Uhr</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
